import { io } from 'socket.io-client';
import { liveGamesStorage } from './rxjs-stores';
import { WS_URL } from './environment';
import { get_proto_root, toUint8Array,mapProtoOddsToBT,coerceToUint8Array,toPair } from './functions';
import { faroLog, faroError, faroEvent, markBuffer, markDiff, setSocketConnected } from './faro';

function onConnect() {
	console.log('connected to socket');
	setSocketConnected(true);
	faroEvent('socket_connected');
	faroLog('info', 'socket connected');
};

const UNKNOWN_DIFF_COOLDOWN_MS = 3000;
const _unknownDiffLastLog = new Map();

function logUnknownDiffRaw(raw, knownStdList = []) {
  const std = String(raw?.std ?? raw?.id ?? "unknown");
  const now = Date.now();
  const last = _unknownDiffLastLog.get(std) || 0;
  if (now - last < UNKNOWN_DIFF_COOLDOWN_MS) return; // throttle
  _unknownDiffLastLog.set(std, now);
  faroLog('warn', 'unknown diff', { std });

  const payload = {
    ts: new Date().toISOString(),
    std,
    raw,                 // ✅ full raw diff
    knownStdList,        // ✅ what client currently knows
  };

  // ✅ console (full object)
  console.groupCollapsed(`[UNKNOWN DIFF RAW] std=${std}`);
  console.log(payload);
  console.groupEnd();

  // ✅ persist last ~50 logs to localStorage (survives refresh)
  try {
    const key = "unknown_diffs_log";
    const arr = JSON.parse(localStorage.getItem(key) || "[]");
    arr.push(payload);
    while (arr.length > 50) arr.shift();
    localStorage.setItem(key, JSON.stringify(arr));
  } catch (_) {}
}

function toPairAny(v) {
	// accept [h,a] or {h,a} or {home,away} or null
	if (Array.isArray(v)) return [Number(v[0] ?? 0), Number(v[1] ?? 0)];
	if (v && typeof v === "object") {
	  const h = Number(v.h ?? v.home ?? v.t1 ?? 0);
	  const a = Number(v.a ?? v.away ?? v.t2 ?? 0);
	  return [Number.isFinite(h) ? h : 0, Number.isFinite(a) ? a : 0];
	}
	return null;
  }
  function coerceStd(v) {
	if (v == null) return null;
  
	// protobufjs Long-like: { low, high, unsigned }
	if (typeof v === "object") {
	  if (typeof v.toString === "function" && (v.low !== undefined || v.high !== undefined)) {
		// safest: use toString() (works even if > 2^32)
		return String(v.toString());
	  }
	  // fallback
	  if (v.low !== undefined) return String(v.low >>> 0);
	}
  
	// bigint
	if (typeof v === "bigint") return v.toString();
  
	// number/string
	return String(v);
  }
  
  function normalizeScorePatch(rawSc) {
	if (!rawSc || typeof rawSc !== "object") return rawSc;
  
	// sometimes diffs come as sc.stats
	const bag = (rawSc.stats && typeof rawSc.stats === "object") ? rawSc.stats : rawSc;
  
	// support many vendor keys
	const goal   = bag.GOAL ?? bag.goal ?? bag.goals ?? bag.score;
	const corner = bag.CORNER ?? bag.corner;
	const yc     = bag.YELLOW_CARD ?? bag.yellowCard;
	const rc     = bag.RED_CARD ?? bag.redCard;
	const h1     = bag.H1 ?? bag.h1;
  
	const out = { ...rawSc };      // keep anything else
	// ensure GOAL etc exist in the shape your UI expects
	const gp = toPairAny(goal);
	if (gp) out.GOAL = gp;
  
	const cp = toPairAny(corner);
	if (cp) out.CORNER = cp;
  
	const ycp = toPairAny(yc);
	if (ycp) out.YELLOW_CARD = ycp;
  
	const rcp = toPairAny(rc);
	if (rcp) out.RED_CARD = rcp;
  
	const h1p = toPairAny(h1);
	if (h1p) out.H1 = h1p;
  
	// if it had stats wrapper, also mirror into stats (optional)
	if (out.stats && typeof out.stats === "object") {
	  const st = { ...out.stats };
	  if (gp) st.GOAL = gp;
	  if (cp) st.CORNER = cp;
	  if (ycp) st.YELLOW_CARD = ycp;
	  if (rcp) st.RED_CARD = rcp;
	  if (h1p) st.H1 = h1p;
	  out.stats = st;
	}
  
	return out;
  }
  function clockStrToTotalSeconds(s) {
	if (typeof s !== "string") return null;
	const t = s.trim();
	if (!t.includes(":")) return null;
	const [mm, ss] = t.split(":");
	const m = Number(mm), sec = Number(ss);
	if (!Number.isFinite(m) || !Number.isFinite(sec)) return null;
	return m * 60 + sec;
  }
  function normalizeDiffForLiveDisplay(raw) {
	if (!raw || typeof raw !== "object") return null;
    console.log(raw);
	const gidRaw =
	raw.std ?? raw.id ??
	(raw.t1?.id && raw.t2?.id ? `${raw.t1.id}-${raw.t2.id}` :
	 raw.t1?.n  && raw.t2?.n  ? `${raw.t1.n}-${raw.t2.n}` :
	 raw.cd ? `code-${raw.cd}` : null);
  
  const gid = coerceStd(gidRaw);
  if (!gid) return null;
  
  const diff = { ...raw, std: gid };
  
  
	// normalize clock/state aliases too (optional but helps)
	// ✅ normalize clock fields so UI never sees seconds="mm:ss"
if (typeof diff.seconds === "string") {
	const total = clockStrToTotalSeconds(diff.seconds);
	if (total != null) {
	  diff.tm = diff.tm ?? diff.seconds;   // keep pretty clock
	  diff.seconds = total;                // numeric seconds
	}
  }
  
  if (typeof diff.tm === "string" && (diff.seconds == null || typeof diff.seconds !== "number")) {
	const total = clockStrToTotalSeconds(diff.tm);
	if (total != null) diff.seconds = total;
  }
  
  // minute: prefer explicit minute; else derive from numeric seconds
  if (diff.minute == null && typeof diff.mn === "number") diff.minute = diff.mn;
  if (diff.minute == null && typeof diff.seconds === "number") diff.minute = Math.floor(diff.seconds / 60);
  
  
	// normalize scores keys -> GOAL pair, etc.
	if (diff.sc) diff.sc = normalizeScorePatch(diff.sc);
	if (diff.scores && !diff.sc) diff.sc = normalizeScorePatch(diff.scores);

	// keep tm as string clock, seconds as number
if (typeof diff.tm === "string") {
	// ok (pretty clock)
  } else if (typeof diff.seconds === "number" && diff.tm == null) {
	// don't invent tm from seconds (you don't have mm:ss here)
	// leave tm null
  }
  
  // minute mirror (numbers only)
  if (typeof diff.minute === "number" && diff.mn == null) diff.mn = diff.minute;
  if (typeof diff.mn === "number" && diff.minute == null) diff.minute = diff.mn;

  if (!diff.sc && diff.currScorePair && diff.currScorePair.home != null && diff.currScorePair.away != null) {
	console.log("score object exists");
	diff.sc = { GOAL: [Number(diff.currScorePair.home) || 0, Number(diff.currScorePair.away) || 0] };
  }

  if (!diff.sc) {
	if (diff.currScorePair?.home != null && diff.currScorePair?.away != null) {
		console.log("diff.currScorePair");
		console.log(diff.currScorePair);
	  diff.sc = { GOAL: [Number(diff.currScorePair.home) || 0, Number(diff.currScorePair.away) || 0] };
	} else if (typeof diff.currScore === "string") {
		console.log("diff.currScore");
		console.log(diff.currScore);
	  const m = diff.currScore.match(/(\d+)\s*[:\-]\s*(\d+)/);
	  if (m) diff.sc = { GOAL: [Number(m[1]), Number(m[2])] };
	}
  }
  
	return diff;
  }
  

  
  // ✅ REPLACE your LiveDisplay onDiffReceivedHandler with this:
  function safeJsonParse(s) {
	try {
	  return JSON.parse(s);
	} catch {
	  return null;
	}
  }
  
  function onDiffReceivedHandler(message) {
	const isStr = typeof message === "string";
	const rawStr = isStr ? message : null;
  
	const raw = isStr ? safeJsonParse(message) : message;
	if (!raw || typeof raw !== "object") return;
	markDiff();
  
	// ✅ LOG: score-ish raw payloads
	if (raw.sc || raw.scores || raw.currScorePair || raw.currScore || raw.stats) {
	  console.groupCollapsed(`[SOCKET SCORE RAW] std=${raw.std ?? raw.id ?? "?"}`);
	  console.log(raw);
	  console.groupEnd();
	}
	const state = raw?.eventStateId ?? raw?.st ?? raw?.matchState ?? raw?.state;
const sid = String(state ?? "");
if (sid === "11001" || sid === "21001" || sid === "11003" || sid === "21003" || sid === "11025" || sid === "21025") {
  console.groupCollapsed(`[DIFF RAW GOAL-STATE] std=${raw.std ?? raw.id ?? "?"} st=${sid}`);
  console.log(raw);
  console.groupEnd();
}

  
	const diff = normalizeDiffForLiveDisplay(raw);
	if (!diff) return;
  
	// ✅ LOG: normalized score payloads
	if (diff.sc || diff.scores || diff.currScorePair || diff.currScore || diff.stats) {
	  console.groupCollapsed(`[SOCKET SCORE NORMALIZED] std=${diff.std}`);
	  console.log({
		std: diff.std,
		sc: diff.sc,
		scores: diff.scores,
		currScorePair: diff.currScorePair,
		currScore: diff.currScore,
		stats: diff.stats,
	  });
	  console.groupEnd();
	}
  
	liveGamesStorage.updateEvent(diff);
  }
  
  
  

const onExpiredGamesEvent = (payload) => {
	if (!payload) return;
	if (payload.startsWith('live_game_')) {
	  liveGamesStorage.removeEvent(payload.replace('live_game_', ''));
	  return;
	}
	if (payload.startsWith('buffer_')) {
	  liveGamesStorage.removeEvent(payload.replace('buffer_', ''));
	  return;
	}
  };  


const onGameReceivedHandler = async (message) => {
	try {
	  const bytes = coerceToUint8Array(message);
	  if (!bytes) return;
  
	  const root = await get_proto_root();
	  const EventType = root.lookupType('bet720_protocol_buffers.Event');
	  let msg;
	  try {
		msg = EventType.decode(bytes);
	  } catch (e) {
		faroError(e, { phase: 'protobuf_decode' });
		return; // bad frame
	  }
  
	  const pojo = EventType.toObject(msg, { arrays: true, objects: true });
	  const items = Array.isArray(pojo?.obj) ? pojo.obj : [];
	  if (items.length === 0) return;
  
	  for (const g of items) {
		if (!g) continue;
  
		// Skip Esoccer
		if (typeof g.inf === 'string' && g.inf.includes('Esoccer')) continue;
  
		// Ensure markets (map odds -> bt if needed)
		if (!Array.isArray(g.bt) || g.bt.length === 0) {
		  g.bt = Array.isArray(g.odds) ? mapProtoOddsToBT(g.odds) : [];
		}
  
		// Normalize scores to what Teller uses (GOAL must be [home, away])
		const s = g.sc || {};
		g.sc = {
			GOAL:       toPair(s.goal),
			CORNER:     toPair(s.corner),
			YELLOW_CARD:toPair(s.yellowCard),
			RED_CARD:   toPair(s.redCard),
			H1:         toPair(s.h1),
			OFFSIDE:    toPair(s.offside),
			SUBSTITUTION: toPair(s.substitution),
			PENALTY:    toPair(s.penalty),
			FREE_KICK:  toPair(s.freeKick),
			THROW_IN:   toPair(s.throwIn),
			GOAL_KICK:  toPair(s.goalKick),
		  };
		  const gidRaw =
		  g.std ?? g.id ??
		  (g.t1?.id && g.t2?.id ? `${g.t1.id}-${g.t2.id}` :
		   g.t1?.n && g.t2?.n ? `${g.t1.n}-${g.t2.n}` :
		   g.cd ? `code-${g.cd}` : null);
		
		const gid = coerceStd(gidRaw);
		if (!gid) continue;
		
		g.std = gid; // ✅ always stable string id
		liveGamesStorage.addOrUpdate({ obj: [g] });
		// Upsert as single-game envelope (what your UI expects)
	  }
	  markBuffer(items.length);
	} catch (e) {
	  console.error('[buffers] failed to handle event buffer:', e);
	  faroError(e, { phase: 'buffers' });
	}
  };
  

const connect = (() => {
	let socket;
	/**
	 * returns a setup function,
	 * closes over the socket
	 */
	return () => {
		if (socket) return socket;
		//initialize if not already initialized	
		socket = io(WS_URL)
		socket.on('connect', onConnect);										// when we connect
		socket.on('disconnect', (reason) => {
			setSocketConnected(false);
			faroEvent('socket_disconnected', { reason });
			faroLog('warn', 'socket disconnected', { reason });
		});
		socket.on('connect_error', (err) => {
			faroEvent('socket_connect_error', { message: err?.message || err });
			faroError(err, { phase: 'connect' });
		});
		socket.on('expired-key', onExpiredGamesEvent);							// when a key expires
		socket.on('buffers', onGameReceivedHandler);							// on initial connect - get all protobuf message
		socket.on('buffer-diffs', onDiffReceivedHandler);						// handle diffs
		return socket;
	};
})();

export default connect;
